import type { User } from '@supabase/supabase-js';
import { Link } from 'react-router-dom';
import { LogIn, Sparkles, UserPlus } from 'lucide-react';
import AccountMenu from './AccountMenu';

interface UserStatusBarProps {
  user: User | null;
  isAdmin: boolean;
  genCredits: number | null;
  cashCredits: number | null;
  loading?: boolean;
  tier?: string | null;
  onLogout: () => void;
  onLoginClick: () => void;
  onSignupClick?: () => void;
}

function formatCredits(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '0';
  if (value >= 1000000) return `${Math.floor(value / 1000) / 100}M`;
  if (value >= 1000) return `${Math.floor(value / 10) / 100}k`;
  return `${Math.floor(value)}`;
}

export default function UserStatusBar({
  user,
  isAdmin,
  genCredits,
  cashCredits,
  loading,
  tier,
  onLogout,
  onLoginClick,
  onSignupClick,
}: UserStatusBarProps) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <div className="h-2 w-2 rounded-full bg-orange-400 animate-pulse" />
        <span className="hidden sm:inline font-medium">Syncing…</span>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onLoginClick}
          className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg border-2 border-orange-500 text-orange-600 hover:bg-orange-50 transition-all"
        >
          <LogIn className="h-4 w-4" />
          <span>Sign in</span>
        </button>
        <button
          type="button"
          onClick={onSignupClick || (() => window.location.href = '/auth?mode=signup&tier=free')}
          className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg bg-orange-500 text-white shadow-md hover:bg-orange-600 transition-all"
        >
          <UserPlus className="h-4 w-4" />
          <span>Sign up</span>
        </button>
      </div>
    );
  }

  const genLabel = formatCredits(genCredits);
  const cashLabel = formatCredits(cashCredits);

  return (
    <div className="flex items-center gap-3 text-xs">
      <Link
        to="/credits"
        className="hidden sm:flex items-center gap-3 px-3 py-1.5 rounded-xl bg-white border border-slate-200 shadow-sm hover:bg-slate-50 transition-colors"
        title="View credits & buy tokens"
      >
        <div className="flex items-center gap-2">
          <div className="h-6 w-6 rounded-lg bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center shadow-sm">
            <Sparkles className="h-3.5 w-3.5 text-white" />
          </div>
          <div className="leading-tight">
            <div className="text-[10px] text-slate-500 font-medium">Generation</div>
            <div className="text-xs font-bold text-orange-600">
              {genLabel}
            </div>
          </div>
        </div>

        <div className="w-px h-6 bg-slate-200" />

        <div className="leading-tight">
          <div className="text-[10px] text-slate-500 font-medium">Cash</div>
          <div className="text-xs font-bold text-emerald-600">
            ${cashLabel}
          </div>
        </div>
      </Link>

      <AccountMenu
        user={user}
        tier={tier}
        genCredits={genCredits}
        cashCredits={cashCredits}
        isAdmin={isAdmin}
        onLogout={onLogout}
      />
    </div>
  );
}
